// HER DASHBOARD ITEM, SURFACE 2(d) — **EVERYTHING ELSE MOVES TO THE BOTTOM, COLLAPSED.**
//
// Her words: *generations by type, room rankings — moves to the BOTTOM as a COLLAPSED section,
// present but out of the way.*
//
// **THE TWO TABLES ARE NAMED BY HER AND NOTHING ELSE MOVED** (scope rule 4). Member growth, Lala
// volume, the stat tiles and the failures table are not in her sentence and are left exactly where
// they are; a round that swept them in as well would be re-organising a screen she described
// precisely.
//
// >>> A COLLAPSED SECTION DRAWS NO BODY AT ALL — NOT HIDDEN WITH CSS. <<< R27's ruling on My Room's
// panels, and it is the same one here for the same reason: `OwnerBars` renders a row per room and a
// row per generation type, and *out of the way* that still lays out every one of them on every visit
// is a section she put away and is still paying for. The consequence is stated where somebody would
// trip over it: **the children are UNMOUNTED**, so nothing may put a form or a scroll position in
// here without knowing that.
//
// **IT IS HOOKLESS AND TAKES `open`/`onToggle` AS PROPS, SO A GATE CALLS IT** (R7/R12) — which is
// the whole reason this is a file rather than twenty lines inside a component that holds four hooks
// and a fetch. The state lives one level up, with the tab that owns the payload.
import { ChevronDown, ChevronRight } from "lucide-react";
import { OwnerBars } from "./OwnerBars";

export type MoreRoom = { room: string; visits: number; generations: number };
export type MoreType = { type: string; count: number };

export function OwnerAnalyticsMore({
  open, onToggle, rooms, byType,
}: {
  open: boolean;
  onToggle: () => void;
  rooms: MoreRoom[];
  byType: MoreType[];
}) {
  return (
    <section className="owner-more" data-more-open={open ? "yes" : "no"}>
      {/* The control says what it opens rather than saying "more", because a section she cannot
          name is one she will not open. `aria-expanded` is what a screen reader has to go on and is
          also what a walk asks before it taps — an unconditional second click closes it. */}
      <button
        type="button"
        className="owner-link owner-more__toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Rooms and generation types
      </button>

      {open ? (
        <div className="owner-two owner-more__body">
          <div>
            <h3 className="owner-detail__sub">Rooms ranked</h3>
            <OwnerBars
              rows={rooms.map((r) => ({
                key: r.room, label: r.room, value: r.visits,
                note: r.generations ? `${r.generations.toLocaleString("en-US")} generated` : "",
              }))}
              empty="No room was visited in this window."
            />
          </div>
          <div>
            <h3 className="owner-detail__sub">Generations by type</h3>
            <OwnerBars
              rows={byType.map((g) => ({ key: g.type, label: g.type, value: g.count }))}
              empty="Nothing was generated in this window."
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
